/**
 * useFokusDropdown - Dropdown menu state and positioning
 */

import { useState, useEffect, useMemo, useCallback } from 'react';

export function useFokusDropdown({ buttonRef }) {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState(new Set(['quick']));

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (buttonRef.current && !buttonRef.current.contains(e.target)) {
        // Check if click is inside the portal menu
        const menu = document.querySelector('.fokus-button__menu');
        if (menu && menu.contains(e.target)) return;
        setIsOpen(false);
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside, true);
      document.addEventListener('keydown', handleEscape);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, buttonRef]);

  // Close when canvas starts panning (trackpad or mouse drag)
  useEffect(() => {
    if (!isOpen) return;

    const handlePanStart = () => setIsOpen(false);
    window.addEventListener('flowchartpanstart', handlePanStart);
    return () => window.removeEventListener('flowchartpanstart', handlePanStart);
  }, [isOpen]);

  // Toggle section expansion
  const toggleSection = useCallback((sectionId) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      next.has(sectionId) ? next.delete(sectionId) : next.add(sectionId);
      return next;
    });
  }, []);

  // Calculate menu position
  const menuPosition = useMemo(() => {
    if (!buttonRef.current || !isOpen) return { top: 0, left: 0 };
    const rect = buttonRef.current.getBoundingClientRect();
    return {
      top: rect.bottom + 4,
      left: rect.left,
    };
  }, [isOpen, buttonRef]);

  return {
    isOpen,
    setIsOpen,
    expandedSections,
    toggleSection,
    menuPosition,
  };
}

export default useFokusDropdown;
